/**
 * CR344 G3: Labels Editor for Components Studio.
 * CR450: Uses canonical component-scoped keys (value_box_title, etc.)
 * 
 * Edits template_json.labels (components-owned).
 * Labels control text shown on CTA buttons, status chips, value boxes, etc.
 */

import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Tags } from "lucide-react";
import type { CardTemplateLabels } from "@/providers/DealCardConfigProvider";
import { CANONICAL_TO_LEGACY } from "@/lib/deal-cards/specV2/defaultLabels";
import type { CanonicalLabelKey } from "@/lib/deal-cards/specV2/defaultLabels";

// ================================================================
// TYPES
// ================================================================

interface LabelsEditorProps {
  labels: Record<string, string>;
  onChange: (update: Record<string, string>) => void;
}

// ================================================================
// LABEL DEFINITIONS (CR450: canonical keys)
// ================================================================

interface LabelDef {
  key: string;
  label: string;
  placeholder: string;
  group: string;
  /** CR450: Show canonical key in UI */
  canonicalKey?: string;
}

const LABEL_DEFS: LabelDef[] = [
  // Value Box Labels (CR450: canonical component-scoped keys)
  { key: "value_box_title", label: "Value Box Title", placeholder: "Effective Value", group: "Value Boxes", canonicalKey: "value_box_title" },
  { key: "reward_box_title", label: "Reward Box Title", placeholder: "Bonus Reward", group: "Value Boxes", canonicalKey: "reward_box_title" },
  { key: "qualifying_spend_box_title", label: "Spend Box Title", placeholder: "Qualifying Spend", group: "Value Boxes", canonicalKey: "qualifying_spend_box_title" },
  { key: "eligibility_box_title", label: "Eligibility Title", placeholder: "Eligibility", group: "Value Boxes", canonicalKey: "eligibility_box_title" },
  
  // CTA / Actions
  { key: "cta_primary", label: "CTA Primary", placeholder: "View deal", group: "Actions" },
  { key: "cta_secondary", label: "CTA Secondary", placeholder: "T&Cs", group: "Actions" },
  
  // Status Chips
  { key: "chip_active", label: "Active Chip", placeholder: "Active", group: "Status" },
  { key: "chip_expired", label: "Expired Chip", placeholder: "Expired", group: "Status" },
  
  // Ribbon
  { key: "ribbon_label", label: "Ribbon Label Override", placeholder: "(use deal type default)", group: "Ribbon" },
];

// Group labels
const GROUPS = ["Value Boxes", "Actions", "Status", "Ribbon"] as const;

// ================================================================
// COMPONENT
// ================================================================

export function LabelsEditor({ labels, onChange }: LabelsEditorProps) {
  const handleChange = (key: string, value: string) => {
    const update = { ...labels, [key]: value };
    
    // CR450: Dual-write — when writing a canonical key, also write the legacy alias
    if (key in CANONICAL_TO_LEGACY) {
      const legacyKey = CANONICAL_TO_LEGACY[key as CanonicalLabelKey];
      update[legacyKey] = value;
      // Also write effective_value_title for value_box_title
      if (key === "value_box_title") {
        update["effective_value_title"] = value;
      }
    }
    
    onChange(update);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Tags className="h-4 w-4 text-muted-foreground" />
        <div>
          <h3 className="text-sm font-semibold">Labels</h3>
          <p className="text-xs text-muted-foreground">
            Override default text shown in deal cards. Leave blank for defaults.
          </p>
        </div>
      </div>

      {GROUPS.map((group) => {
        const groupLabels = LABEL_DEFS.filter((d) => d.group === group);
        if (groupLabels.length === 0) return null;

        return (
          <div key={group} className="space-y-3">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                {group}
              </Badge>
            </div>

            <div className="space-y-2">
              {groupLabels.map((def) => (
                <div key={def.key} className="flex items-center gap-3">
                  <div className="w-[140px] flex-shrink-0 text-right">
                    <Label className="text-xs font-medium text-muted-foreground">
                      {def.label}
                    </Label>
                    {def.canonicalKey && (
                      <span className="block text-[9px] text-muted-foreground/50 font-mono">
                        {def.canonicalKey}
                      </span>
                    )}
                  </div>
                  <Input
                    value={labels[def.key] ?? ""}
                    onChange={(e) => handleChange(def.key, e.target.value)}
                    placeholder={def.placeholder}
                    className="h-8 text-xs flex-1"
                  />
                </div>
              ))}
            </div>

            <Separator />
          </div>
        );
      })}

      <p className="text-[10px] text-muted-foreground">
        These labels are saved to template_json.labels and affect how text renders on the deal card.
      </p>
    </div>
  );
}

export default LabelsEditor;
